/**
 * 📝 [Phase 103.1 — 2026-07-09] Formulaire d'évaluation employé — v2 workflow.
 *
 * Nouveautés Phase 103.1 :
 *  - Fetch du template éditable au montage (GET /api/evaluation-criteria-template)
 *  - Bouton "Modifier les critères d'évaluation" → mode édition via CriteriaTemplateEditor
 *  - Dialog de confirmation à la sauvegarde SI critères modifiés :
 *      ○ Enregistrer uniquement pour cette évaluation (défaut)
 *      ○ Enregistrer et mettre à jour le modèle pour les prochaines évaluations
 *  - Workflow 4 statuts : brouillon → prete → publiee → archivee
 *  - Boutons contextuels : Finaliser (→prete), Rendre visible (→publiee),
 *    Retour brouillon, Imprimer PDF
 *  - Badge statut coloré
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Save,
  CheckCircle2,
  Loader2,
  Star,
  Pencil,
  Eye,
  Printer,
  Undo2,
  Send,
} from "lucide-react";
import { CriteriaTemplateEditor } from "./CriteriaTemplateEditor";

const API_URL =
  (typeof window !== "undefined" && window.__API_URL__) ||
  process.env.REACT_APP_BACKEND_URL ||
  "";

function getAuthToken() {
  try {
    return (
      localStorage.getItem("auth_token") ||
      sessionStorage.getItem("auth_token") ||
      ""
    );
  } catch {
    return "";
  }
}
const authHeader = () => {
  const t = getAuthToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const STATUT_META = {
  brouillon: { label: "Brouillon", className: "bg-slate-100 text-slate-700 border-slate-300" },
  prete: { label: "Prête", className: "bg-amber-100 text-amber-800 border-amber-300" },
  publiee: { label: "Publiée (visible)", className: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  archivee: { label: "Archivée", className: "bg-slate-200 text-slate-600 border-slate-400" },
};

function ScaleButtons({ value, onChange, labels, testId }) {
  return (
    <div className="flex items-center gap-1" data-testid={testId}>
      {[1, 2, 3, 4, 5].map((v) => (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          title={labels[v]}
          data-testid={`${testId}-${v}`}
          className={`h-7 w-7 rounded-full border text-xs font-semibold transition-colors ${
            value === v
              ? "bg-blue-600 text-white border-blue-700 shadow-sm"
              : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"
          }`}
        >
          {v}
        </button>
      ))}
    </div>
  );
}

function noteColor(note) {
  if (note == null) return "bg-slate-200 text-slate-500";
  if (note < 2.5) return "bg-red-100 text-red-800 border-red-300";
  if (note < 3.5) return "bg-orange-100 text-orange-800 border-orange-300";
  if (note < 4) return "bg-yellow-100 text-yellow-800 border-yellow-300";
  return "bg-emerald-100 text-emerald-800 border-emerald-300";
}

/**
 * Dialog de confirmation : critères modifiés → propager au template ou non ?
 */
function CriteriaSaveDialog({ open, onOpenChange, onConfirm }) {
  const [choice, setChoice] = useState("this_only");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="criteria-save-dialog">
        <DialogHeader>
          <DialogTitle>Enregistrement des modifications</DialogTitle>
          <DialogDescription>
            Vous avez modifié les critères d&apos;évaluation. Que voulez-vous faire ?
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <label className="flex gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
            <input
              type="radio"
              name="criteres-save-choice"
              value="this_only"
              checked={choice === "this_only"}
              onChange={(e) => setChoice(e.target.value)}
              data-testid="criteria-save-choice-this-only"
              className="mt-1"
            />
            <div>
              <p className="text-sm font-medium text-slate-800">
                Enregistrer uniquement pour cette évaluation
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Recommandé — les modifications ne s&apos;appliquent qu&apos;à cette éval spécifique.
                Le modèle par défaut reste inchangé pour les autres.
              </p>
            </div>
          </label>
          <label className="flex gap-3 p-3 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer">
            <input
              type="radio"
              name="criteres-save-choice"
              value="update_template"
              checked={choice === "update_template"}
              onChange={(e) => setChoice(e.target.value)}
              data-testid="criteria-save-choice-update-template"
              className="mt-1"
            />
            <div>
              <p className="text-sm font-medium text-slate-800">
                Enregistrer et mettre à jour le modèle pour les prochaines évaluations
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Toutes les évaluations à venir utiliseront ces nouveaux critères par défaut.
              </p>
            </div>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="criteria-save-cancel">
            Annuler
          </Button>
          <Button
            className="bg-blue-600 hover:bg-blue-700"
            onClick={() => {
              onConfirm(choice);
              onOpenChange(false);
            }}
            data-testid="criteria-save-confirm"
          >
            Confirmer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function EmployeeEvaluationForm({
  open,
  onOpenChange,
  employeeId,
  employeeName,
  initialEvaluation,
  config,
  onSaved,
}) {
  const isEdit = !!initialEvaluation?.id;
  const [saving, setSaving] = useState(false);
  const [busyAction, setBusyAction] = useState(null); // 'mark_ready' | 'publish' | 'back_to_draft'
  const [annee, setAnnee] = useState(new Date().getFullYear());
  const [periodeDebut, setPeriodeDebut] = useState(`${new Date().getFullYear()}-01-01`);
  const [periodeFin, setPeriodeFin] = useState(`${new Date().getFullYear()}-12-31`);
  const [criteres, setCriteres] = useState({});
  // 🆕 [Hotfix v13.36 — 2026-07-09] Bug #5 : commentaires textuels par critère.
  // Structure : { [catCode]: { [critCode]: string } }. Champ optionnel — les évaluations
  // existantes sans commentaires fonctionnent sans changement.
  const [commentairesCriteres, setCommentairesCriteres] = useState({});
  const [pointsForts, setPointsForts] = useState("");
  const [axesAmelio, setAxesAmelio] = useState("");
  const [objectifs, setObjectifs] = useState("");
  const [commentaires, setCommentaires] = useState("");
  const [noteManuelle, setNoteManuelle] = useState("");
  const [justification, setJustification] = useState("");
  const [evalId, setEvalId] = useState(null);
  const [statut, setStatut] = useState("brouillon");

  // 🆕 [Phase 103.1] Snapshot des critères (structure liste)
  const [snapshot, setSnapshot] = useState(null);
  const [templateInitial, setTemplateInitial] = useState(null);
  const [editingCriteria, setEditingCriteria] = useState(false);
  const [criteresModifies, setCriteresModifies] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [pendingSaveKind, setPendingSaveKind] = useState(null); // 'draft' | 'ready'

  // Fetch template au montage
  const loadTemplate = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/evaluation-criteria-template`, {
        headers: authHeader(),
      });
      setTemplateInitial(res.data);
      return res.data;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    (async () => {
      if (initialEvaluation) {
        setEvalId(initialEvaluation.id);
        setStatut(initialEvaluation.statut || "brouillon");
        setAnnee(initialEvaluation.annee || new Date().getFullYear());
        setPeriodeDebut(initialEvaluation.periode_debut || "");
        setPeriodeFin(initialEvaluation.periode_fin || "");
        setCriteres(initialEvaluation.criteres || {});
        setCommentairesCriteres(initialEvaluation.commentaires_criteres || {});
        setPointsForts(initialEvaluation.points_forts || "");
        setAxesAmelio(initialEvaluation.axes_amelioration || "");
        setObjectifs(initialEvaluation.objectifs_suivants || "");
        setCommentaires(initialEvaluation.commentaires_evaluateur || "");
        setNoteManuelle(
          initialEvaluation.note_globale_manuelle != null
            ? String(initialEvaluation.note_globale_manuelle)
            : ""
        );
        setJustification(initialEvaluation.note_globale_justification || "");
        // Snapshot : celui de l'éval si présent, sinon load template
        if (initialEvaluation.criteres_snapshot) {
          setSnapshot(initialEvaluation.criteres_snapshot);
        } else {
          const t = await loadTemplate();
          if (t) setSnapshot({ categories: t.categories, template_version: t.version });
        }
      } else {
        setEvalId(null);
        setStatut("brouillon");
        setAnnee(new Date().getFullYear());
        setPeriodeDebut(`${new Date().getFullYear()}-01-01`);
        setPeriodeFin(`${new Date().getFullYear()}-12-31`);
        setCriteres({});
        setCommentairesCriteres({});
        setPointsForts("");
        setAxesAmelio("");
        setObjectifs("");
        setCommentaires("");
        setNoteManuelle("");
        setJustification("");
        const t = await loadTemplate();
        if (t) setSnapshot({ categories: t.categories, template_version: t.version });
      }
      setEditingCriteria(false);
      setCriteresModifies(false);
    })();
  }, [open, initialEvaluation, loadTemplate]);

  const setCritere = (catCode, critCode, val) => {
    setCriteres((prev) => ({
      ...prev,
      [catCode]: { ...(prev[catCode] || {}), [critCode]: val },
    }));
  };

  // 🆕 [Hotfix v13.36] Bug #5 : setter pour commentaire textuel par critère.
  const setCommentaireCritere = (catCode, critCode, val) => {
    setCommentairesCriteres((prev) => {
      const catObj = { ...(prev[catCode] || {}) };
      if (val && val.trim()) {
        catObj[critCode] = val;
      } else {
        // Nettoyer : si vide, on retire la clé (rétrocompat + payload propre)
        delete catObj[critCode];
      }
      const next = { ...prev };
      if (Object.keys(catObj).length) {
        next[catCode] = catObj;
      } else {
        delete next[catCode];
      }
      return next;
    });
  };

  const scoresList = useMemo(() => {
    return Object.values(criteres || {})
      .flatMap((c) => Object.values(c || {}))
      .filter((v) => typeof v === "number");
  }, [criteres]);

  const noteCalc = useMemo(() => {
    if (!scoresList.length) return null;
    return Math.round((scoresList.reduce((a, b) => a + b, 0) / scoresList.length) * 10) / 10;
  }, [scoresList]);

  const noteFinale = useMemo(() => {
    const nm = parseFloat(noteManuelle);
    return !isNaN(nm) ? nm : noteCalc;
  }, [noteCalc, noteManuelle]);

  const nbCriteresNotes = scoresList.length;

  const isReadOnly = statut === "publiee" || statut === "archivee";

  const buildPayload = (includeSnapshot = false) => {
    const p = {
      annee,
      periode_debut: periodeDebut,
      periode_fin: periodeFin,
      criteres,
      commentaires_criteres: commentairesCriteres,  // 🆕 [Hotfix v13.36] Bug #5
      note_globale_manuelle: noteManuelle === "" ? null : parseFloat(noteManuelle),
      note_globale_justification: justification,
      points_forts: pointsForts,
      axes_amelioration: axesAmelio,
      objectifs_suivants: objectifs,
      commentaires_evaluateur: commentaires,
    };
    if (includeSnapshot && snapshot) p.criteres_snapshot = snapshot;
    return p;
  };

  const doSave = async (updateTemplate = false) => {
    if (noteManuelle !== "" && !justification.trim()) {
      toast.error("Une justification est requise pour la note manuelle");
      return null;
    }
    setSaving(true);
    try {
      // Étape 1 : propager au template si demandé
      if (updateTemplate && snapshot) {
        try {
          await axios.put(
            `${API_URL}/api/evaluation-criteria-template`,
            { categories: snapshot.categories },
            { headers: authHeader() }
          );
          toast.success("Modèle mis à jour pour les prochaines évaluations");
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Erreur mise à jour du modèle");
        }
      }
      // Étape 2 : save l'éval (POST ou PUT selon existence)
      const payload = buildPayload(criteresModifies);
      let saved;
      if (evalId) {
        const res = await axios.put(`${API_URL}/api/evaluations/${evalId}`, payload, { headers: authHeader() });
        saved = res.data;
      } else {
        const res = await axios.post(`${API_URL}/api/employees/${employeeId}/evaluations`, payload, { headers: authHeader() });
        saved = res.data;
        setEvalId(saved.id);
      }
      setStatut(saved.statut || "brouillon");
      setCriteresModifies(false);
      onSaved?.(saved);
      return saved;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de la sauvegarde");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    if (criteresModifies) {
      setPendingSaveKind("draft");
      setShowSaveDialog(true);
      return;
    }
    const saved = await doSave(false);
    if (saved) toast.success("Brouillon sauvegardé");
  };

  const handleMarkReady = async () => {
    if (!nbCriteresNotes) {
      toast.error("Au moins un critère doit être noté avant de marquer prêt");
      return;
    }
    if (criteresModifies) {
      setPendingSaveKind("ready");
      setShowSaveDialog(true);
      return;
    }
    const saved = await doSave(false);
    if (!saved) return;
    setBusyAction("mark_ready");
    try {
      const res = await axios.post(`${API_URL}/api/evaluations/${saved.id}/mark-ready`, {}, { headers: authHeader() });
      setStatut(res.data.statut);
      toast.success("Évaluation marquée comme prête (non visible pour l'employé)");
      onSaved?.(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusyAction(null);
    }
  };

  const handleConfirmSaveDialog = async (choice) => {
    const kind = pendingSaveKind;
    setPendingSaveKind(null);
    const updateTemplate = choice === "update_template";
    const saved = await doSave(updateTemplate);
    if (!saved) return;
    if (kind === "ready") {
      setBusyAction("mark_ready");
      try {
        const res = await axios.post(`${API_URL}/api/evaluations/${saved.id}/mark-ready`, {}, { headers: authHeader() });
        setStatut(res.data.statut);
        toast.success("Évaluation marquée comme prête (non visible pour l'employé)");
        onSaved?.(res.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Erreur");
      } finally {
        setBusyAction(null);
      }
    } else {
      toast.success("Brouillon sauvegardé");
    }
  };

  const handleBackToDraft = async () => {
    if (!evalId) return;
    setBusyAction("back_to_draft");
    try {
      const res = await axios.post(`${API_URL}/api/evaluations/${evalId}/back-to-draft`, {}, { headers: authHeader() });
      setStatut(res.data.statut);
      toast.success("Retour en brouillon");
      onSaved?.(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusyAction(null);
    }
  };

  const handlePublish = async () => {
    if (!evalId) return;
    if (!window.confirm(
      "Confirmer la publication de cette évaluation à l'employé ?\n\n"
      + "Il pourra la consulter dès maintenant dans son espace."
    )) return;
    setBusyAction("publish");
    try {
      const res = await axios.post(`${API_URL}/api/evaluations/${evalId}/publish`, {}, { headers: authHeader() });
      setStatut(res.data.statut);
      toast.success("Évaluation rendue visible à l'employé");
      onSaved?.(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setBusyAction(null);
    }
  };

  const handlePrintPdf = async () => {
    if (!evalId) {
      toast.error("Enregistre d'abord l'évaluation avant d'imprimer");
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/api/evaluations/${evalId}/pdf`, {
        headers: authHeader(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener");
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la génération du PDF");
    }
  };

  const categoriesToRender = (snapshot?.categories) || [];
  const echelle = config?.echelle || {
    1: "Insatisfaisant",
    2: "À améliorer",
    3: "Conforme",
    4: "Bien",
    5: "Excellent",
  };
  const statutMeta = STATUT_META[statut] || STATUT_META.brouillon;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-w-4xl max-h-[90vh] overflow-y-auto"
          data-testid="evaluation-form-modal"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap">
              <span>{isEdit ? "Évaluation" : "Nouvelle évaluation"}</span>
              {employeeName && (
                <span className="text-slate-500 font-normal text-base">— {employeeName}</span>
              )}
              <Badge
                className={`text-xs border ${statutMeta.className}`}
                data-testid="eval-statut-badge"
              >
                {statutMeta.label}
              </Badge>
            </DialogTitle>
            <DialogDescription>
              5 catégories × 4 critères notés de 1 à 5. Modifie les critères si besoin
              via le bouton ci-dessous.
            </DialogDescription>
          </DialogHeader>

          {/* Mode édition critères */}
          {editingCriteria ? (
            <CriteriaTemplateEditor
              template={snapshot}
              onChange={(next) => {
                setSnapshot({ ...(snapshot || {}), categories: next.categories });
                setCriteresModifies(true);
              }}
              onCancel={() => {
                // Restaurer le snapshot original (celui de l'éval ou du template initial)
                if (initialEvaluation?.criteres_snapshot) {
                  setSnapshot(initialEvaluation.criteres_snapshot);
                } else if (templateInitial) {
                  setSnapshot({ categories: templateInitial.categories, template_version: templateInitial.version });
                }
                setCriteresModifies(false);
                setEditingCriteria(false);
              }}
              onDone={() => setEditingCriteria(false)}
            />
          ) : (
            <div className="space-y-6 py-2">
              {/* Métadonnées */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-3 border border-slate-200 rounded-lg bg-slate-50">
                <div>
                  <Label>Année</Label>
                  <Input
                    type="number" min={2020} max={2100}
                    value={annee}
                    onChange={(e) => setAnnee(parseInt(e.target.value) || annee)}
                    disabled={isReadOnly}
                    data-testid="eval-annee-input"
                  />
                </div>
                <div>
                  <Label>Période — début</Label>
                  <Input type="date" value={periodeDebut} onChange={(e) => setPeriodeDebut(e.target.value)} disabled={isReadOnly} data-testid="eval-periode-debut-input"/>
                </div>
                <div>
                  <Label>Période — fin</Label>
                  <Input type="date" value={periodeFin} onChange={(e) => setPeriodeFin(e.target.value)} disabled={isReadOnly} data-testid="eval-periode-fin-input"/>
                </div>
              </div>

              {/* Bouton "Modifier les critères" */}
              {!isReadOnly && (
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="text-xs text-slate-500">
                    {criteresModifies && (
                      <span className="text-amber-600 font-medium">
                        ⚠ Critères modifiés — le choix de propagation te sera demandé à la sauvegarde
                      </span>
                    )}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingCriteria(true)}
                    data-testid="btn-edit-criteria"
                  >
                    <Pencil className="w-4 h-4 mr-1" /> Modifier les critères d&apos;évaluation
                  </Button>
                </div>
              )}

              {/* Grille de critères (rendus depuis le snapshot) */}
              <div className="space-y-4" data-testid="eval-criteres-grid">
                {categoriesToRender.map((cat) => (
                  <div
                    key={cat.code}
                    className="border border-slate-200 rounded-lg p-3 bg-white"
                    data-testid={`eval-cat-${cat.code}`}
                  >
                    <h4 className="font-semibold text-sm text-slate-800 mb-2">{cat.label}</h4>
                    <div className="space-y-2">
                      {(cat.criteres || []).map((crit) => {
                        const val = criteres?.[cat.code]?.[crit.code];
                        const comm = commentairesCriteres?.[cat.code]?.[crit.code] || "";
                        return (
                          <div
                            key={crit.code}
                            className="flex flex-col gap-1.5 py-1.5 border-b border-slate-100 last:border-b-0"
                          >
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                              <span className="text-sm text-slate-700">{crit.label}</span>
                              <div className="flex items-center gap-2">
                                <ScaleButtons
                                  value={val}
                                  onChange={(v) => setCritere(cat.code, crit.code, v)}
                                  labels={echelle}
                                  testId={`eval-scale-${cat.code}-${crit.code}`}
                                />
                                {val != null && (
                                  <span className="text-xs text-slate-400 min-w-[110px]">
                                    {echelle[val]}
                                  </span>
                                )}
                              </div>
                            </div>
                            {/* 🆕 [Hotfix v13.36 — 2026-07-09] Bug #5 : Textarea compact pour commentaire par critère */}
                            <Textarea
                              value={comm}
                              onChange={(e) => setCommentaireCritere(cat.code, crit.code, e.target.value)}
                              placeholder="Commentaire sur ce critère (optionnel)"
                              rows={2}
                              disabled={isReadOnly}
                              maxLength={2000}
                              className="text-xs resize-none border-slate-200 bg-slate-50/60 placeholder:text-slate-400 focus:bg-white"
                              data-testid={`eval-commentaire-${cat.code}-${crit.code}`}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* Note globale */}
              <div className="border border-slate-200 rounded-lg p-4 bg-white" data-testid="eval-note-globale">
                <div className="flex items-center gap-3 mb-3">
                  <Star className="w-5 h-5 text-amber-500" />
                  <h4 className="font-semibold text-sm text-slate-800">Note globale</h4>
                  <Badge className={`ml-auto text-sm font-bold border ${noteColor(noteFinale)}`} data-testid="eval-note-badge">
                    {noteFinale != null ? `${noteFinale} / 5` : "—"}
                  </Badge>
                </div>
                <p className="text-xs text-slate-500 mb-2">
                  Calculée : {noteCalc != null ? `${noteCalc} / 5` : "—"} (moyenne de {nbCriteresNotes} critère{nbCriteresNotes > 1 ? "s" : ""})
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Note manuelle (override optionnel)</Label>
                    <Input
                      type="number" step="0.1" min={1} max={5}
                      value={noteManuelle}
                      onChange={(e) => setNoteManuelle(e.target.value)}
                      placeholder="Laisser vide pour utiliser le calcul"
                      disabled={isReadOnly}
                      data-testid="eval-note-manuelle-input"
                    />
                  </div>
                  <div>
                    <Label>Justification {noteManuelle !== "" && (<span className="text-red-500">*</span>)}</Label>
                    <Input
                      value={justification}
                      onChange={(e) => setJustification(e.target.value)}
                      placeholder="Ex. Progression exceptionnelle du trimestre"
                      disabled={isReadOnly}
                      data-testid="eval-justification-input"
                    />
                  </div>
                </div>
              </div>

              {/* Textes libres */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Points forts</Label>
                  <Textarea rows={3} value={pointsForts} onChange={(e) => setPointsForts(e.target.value)} disabled={isReadOnly} data-testid="eval-points-forts-input"/>
                </div>
                <div>
                  <Label>Axes d&apos;amélioration</Label>
                  <Textarea rows={3} value={axesAmelio} onChange={(e) => setAxesAmelio(e.target.value)} disabled={isReadOnly} data-testid="eval-axes-amelio-input"/>
                </div>
                <div>
                  <Label>Objectifs pour la période suivante</Label>
                  <Textarea rows={3} value={objectifs} onChange={(e) => setObjectifs(e.target.value)} disabled={isReadOnly} data-testid="eval-objectifs-input"/>
                </div>
                <div>
                  <Label>Commentaires de l&apos;évaluateur</Label>
                  <Textarea rows={3} value={commentaires} onChange={(e) => setCommentaires(e.target.value)} disabled={isReadOnly} data-testid="eval-commentaires-input"/>
                </div>
              </div>
            </div>
          )}

          {/* Actions contextuelles selon statut */}
          {!editingCriteria && (
            <DialogFooter className="gap-2 flex-wrap">
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="eval-cancel-btn">
                Fermer
              </Button>

              {/* Bouton Imprimer PDF : dispo dès qu'un evalId existe et statut ≥ prete */}
              {evalId && (statut === "prete" || statut === "publiee" || statut === "archivee") && (
                <Button
                  variant="outline"
                  onClick={handlePrintPdf}
                  data-testid="btn-print-pdf"
                >
                  <Printer className="w-4 h-4 mr-1" /> Imprimer
                </Button>
              )}

              {statut === "brouillon" && (
                <>
                  <Button
                    variant="outline"
                    onClick={handleSaveDraft}
                    disabled={saving || busyAction}
                    data-testid="eval-save-draft-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                    Sauvegarder le brouillon
                  </Button>
                  <Button
                    className="bg-amber-500 hover:bg-amber-600"
                    onClick={handleMarkReady}
                    disabled={saving || busyAction || !nbCriteresNotes}
                    data-testid="eval-mark-ready-btn"
                  >
                    {busyAction === "mark_ready" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                    Finaliser (marquer prête)
                  </Button>
                </>
              )}

              {statut === "prete" && (
                <>
                  <Button
                    variant="outline"
                    onClick={handleBackToDraft}
                    disabled={busyAction}
                    data-testid="eval-back-to-draft-btn"
                  >
                    <Undo2 className="w-4 h-4 mr-1" /> Revenir en brouillon
                  </Button>
                  <Button
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={handlePublish}
                    disabled={busyAction}
                    data-testid="eval-publish-btn"
                  >
                    {busyAction === "publish" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                    Rendre visible par l&apos;employé
                  </Button>
                </>
              )}

              {statut === "publiee" && (
                <span className="text-xs text-emerald-700 flex items-center gap-1 ml-2">
                  <Eye className="w-3 h-3" /> Visible par l&apos;employé
                </span>
              )}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <CriteriaSaveDialog
        open={showSaveDialog}
        onOpenChange={setShowSaveDialog}
        onConfirm={handleConfirmSaveDialog}
      />
    </>
  );
}

export default EmployeeEvaluationForm;
